import "./index.css";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import NewspaperOutlinedIcon from "@mui/icons-material/NewspaperOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  alpha,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { animated as a } from "@react-spring/web";
import { DeviceType } from "@virtualprogaming/js-public-sdk";
import { get, map } from "lodash-es";
import React, { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";

import { NOTIFICATIONS_SETTINGS } from "~/constants";
import { STRUCTURES, SUPPORT } from "~/data";
import DiscordIcon from "~/images/icons/discord-icon-svgrepo-com.svg";
import VPGMainLogo from "~/images/icons/vpg-icon-CucS85T0_copy.png";
import { useFcmUnsubscribe, useUser } from "~/queries";
import { useLogout } from "~/queries/auth";
import {
  HeaderAnnouncements,
  Icon,
  SelectLanguage,
  UserAvatar,
} from "~/ui/components";
import {
  useCurrentUser,
  useIsLoggedIn,
  useScreenMd,
  useScreenSm,
} from "~/utils";

const Tools: React.FC<{
  openLanguageMenu: () => void;
  onOpenChat: () => void;
}> = ({ openLanguageMenu }) => {
  const logout = useLogout();
  const currentUser = useCurrentUser();
  const history = useHistory();
  const { pathname } = useLocation();
  const { mutateAsync: fcmUnubscribe } = useFcmUnsubscribe();
  const theme = useTheme();

  const { data: currentUserDetails } = useUser(
    get(currentUser, "username", ""),
    { enabled: !!currentUser }
  );

  const [avatarAnchor, setAvatarAnchor] = useState<null | HTMLElement>(null);

  const onContractClick = () => {
    history.push("/dashboard/contracts");
  };

  const handleLogout = () => {
    if (confirm("Are you sure you want to logout?")) {
      fcmUnubscribe(DeviceType.Web);
      logout();
      localStorage.removeItem("show_again_premium");
      localStorage.removeItem(NOTIFICATIONS_SETTINGS);
    }
  };

  const onProfileClick = () => {
    if (currentUserDetails?.username) {
      history.push(`/user/${currentUserDetails.username}`);
    }
    setAvatarAnchor(null);
  };

  const onStatsClick = () => {
    history.push("/dashboard/stats");
    setAvatarAnchor(null);
  };

  const onDashboardClick = () => {
    history.push("/dashboard");
    setAvatarAnchor(null);
  };

  const toolButtonStyle = {
    minWidth: "auto",
    width: 40,
    height: 40,
    borderRadius: "8px",
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    "&.active": {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
      color: theme.palette.primary.main,
      fontWeight: 700,
      boxShadow: `0 2px 0 ${theme.palette.primary.main}`,
    },
  };

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexShrink: 0,
        gap: 1,
      }}
    >
      <Tooltip title="Discord">
        <IconButton
          sx={toolButtonStyle}
          component="a"
          href="https://discord.gg/7njMWQe2Ss"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Box
            component="img"
            src={DiscordIcon}
            alt="Discord"
            sx={{ width: 22, height: 22, display: "block" }}
          />
        </IconButton>
      </Tooltip>

      {currentUser?.user_id === 3 && (
        <Tooltip title="Team Applications">
          <IconButton
            sx={{
              ...toolButtonStyle,
              ...(pathname.includes("admin/team-applications") && {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
              }),
            }}
            component={Link}
            to="/admin/team-applications"
          >
            <AssignmentOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip
        title={<FormattedMessage defaultMessage="Language" id="y1Z3or" />}
      >
        <IconButton sx={toolButtonStyle} onClick={openLanguageMenu}>
          <Icon icon={["far", "globe"]} size="sm" />
        </IconButton>
      </Tooltip>

      <Tooltip
        title={<FormattedMessage defaultMessage="Contract" id="nV7Cv+" />}
      >
        <IconButton
          sx={{
            ...toolButtonStyle,
            ...(pathname.includes("contract") && {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            }),
          }}
          onClick={onContractClick}
        >
          <DescriptionOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip
        title={<FormattedMessage defaultMessage="Profile" id="itPgxd" />}
      >
        <IconButton
          sx={{
            ...toolButtonStyle,
            padding: 0.5,
          }}
          onClick={(e) => setAvatarAnchor(e.currentTarget)}
        >
          <UserAvatar avatar={currentUserDetails?.image_id} variant="XS" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={avatarAnchor}
        open={Boolean(avatarAnchor)}
        onClose={() => setAvatarAnchor(null)}
        PaperProps={{
          sx: {
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            borderRadius: "8px",
            minWidth: 180,
          },
        }}
      >
        <MenuItem onClick={onProfileClick}>
          <PersonOutlineOutlinedIcon fontSize="small" />
          <Typography sx={{ ml: 1.5, fontSize: "0.95rem" }}>
            <FormattedMessage defaultMessage="Profile" id="itPgxd" />
          </Typography>
        </MenuItem>
        <MenuItem onClick={onDashboardClick}>
          <DashboardOutlinedIcon fontSize="small" />
          <Typography sx={{ ml: 1.5, fontSize: "0.95rem" }}>
            <FormattedMessage defaultMessage="Dashboard" id="hzSNj4" />
          </Typography>
        </MenuItem>
        <MenuItem onClick={onStatsClick}>
          <BarChartOutlinedIcon fontSize="small" />
          <Typography sx={{ ml: 1.5, fontSize: "0.95rem" }}>
            <FormattedMessage defaultMessage="Stats" id="U86B6w" />
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAvatarAnchor(null);
            handleLogout();
          }}
          sx={{
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          <Icon icon={["fal", "sign-out"]} size="sm" />
          <Typography sx={{ ml: 1.5, fontSize: "0.95rem" }}>
            <FormattedMessage defaultMessage="Logout" id="C81/uG" />
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export interface LinkProps {
  to: string;
  icon: React.ReactNode;
  text: React.ReactElement;
  exact?: boolean;
}

const homeTab = {
  to: "/",
  icon: <HomeOutlinedIcon fontSize="small" />,
  text: <FormattedMessage defaultMessage="Home" id="ejEGdx" />,
  exact: true,
};

const links: LinkProps[] = [
  {
    to: "/competitions",
    icon: <EmojiEventsOutlinedIcon fontSize="small" />,
    text: <FormattedMessage defaultMessage="Competitions" id="Kq+T7I" />,
  },
  {
    to: "/news",
    icon: <NewspaperOutlinedIcon fontSize="small" />,
    text: <FormattedMessage defaultMessage="News" id="NZyhSX" />,
  },
  {
    to: "/shop",
    icon: <ShoppingCartOutlinedIcon fontSize="small" />,
    text: <FormattedMessage defaultMessage="Shop" id="Dxd1uB" />,
  },
  {
    to: "/team/calendar",
    icon: <CalendarTodayOutlinedIcon fontSize="small" />,
    text: <FormattedMessage defaultMessage="My Calendar" id="DQmMzS" />,
  },
];

interface NavigationMenuProps {
  onClose?: () => void;
  openLanguageMenu: () => void;
  onOpenChat: () => void;
  isDesktop: boolean;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  onClose = () => undefined,
  openLanguageMenu,
  onOpenChat,
  isDesktop,
}) => {
  const currentUser = useCurrentUser();
  const isMobile = useScreenSm();
  const isDesktopView = useScreenMd();
  const isLoggedIn = useIsLoggedIn();
  const { data: currentUserDetails } = useUser(
    get(currentUser, "username", ""),
    { enabled: isLoggedIn }
  );
  const intl = useIntl();
  const theme = useTheme();
  const [structuresAnchor, setStructuresAnchor] = useState<null | HTMLElement>(
    null
  );
  const [supportAnchor, setSupportAnchor] = useState<null | HTMLElement>(null);
  const [rankingsAnchor, setRankingsAnchor] = useState<null | HTMLElement>(
    null
  );

  const openLink = (link: string) => window.open(link, "_blank");

  // Filter out calendar link for desktop if not logged in
  const filteredLinks = useMemo(() => {
    if (isDesktopView && !isLoggedIn) {
      return links.filter((link) => link.to !== "/team/calendar");
    }
    return links;
  }, [isDesktopView, isLoggedIn]);

  const tabs = useMemo(
    () => (isDesktopView ? filteredLinks : [homeTab, ...links]),
    [isDesktopView, filteredLinks]
  );

  const navButtonStyle = {
    color: theme.palette.text.primary,
    textTransform: "none" as const,
    fontWeight: 500,
    fontSize: "0.9rem",
    padding: "10px 18px",
    minHeight: "auto",
    borderRadius: "8px",
    whiteSpace: "nowrap",
    position: "relative",
    transition: "all 0.2s ease-in-out",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: "50%",
      width: 0,
      height: "2px",
      backgroundColor: theme.palette.primary.main,
      transition: "all 0.2s ease-in-out",
      transform: "translateX(-50%)",
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
      transform: "translateY(-1px)",
      "&::after": {
        width: "80%",
      },
    },
    "&.active": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      color: theme.palette.primary.main,
      fontWeight: 700,
      "&::after": {
        width: "100%",
      },
    },
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        backgroundColor: alpha(theme.palette.background.default, 0.95),
        backdropFilter: "blur(20px)",
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
      }}
    >
      {/* Header Top Section */}
      <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: 80,
            position: "relative",
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              flexShrink: 0,
              mr: { xs: 0, md: 2, xl: 3 },
            }}
          >
            <Link to="/">
              <Box
                component="img"
                src={VPGMainLogo}
                alt="VPG Logo"
                sx={{
                  height: {
                    xs: 20,
                    sm: 20,
                    md: 20,
                    lg: 24,
                  },
                  width: "auto",
                  transition: "height 0.2s",
                  "@media (max-width:900px)": {
                    height: 20,
                    maxWidth: 220,
                    width: "100%",
                  },
                }}
              />
            </Link>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              flex: 1,
              ml: -2,
              gap: 1,
              flexWrap: "nowrap",
              overflow: "hidden",
              minWidth: 0,
              maxWidth: "100%",
              justifyContent: "center",
              "& .MuiButton-root": {
                flexShrink: 0,
              },
            }}
          >
            {map(tabs, (link: LinkProps) => (
              <Button
                key={link.to}
                component={NavLink}
                to={link.to}
                exact={link.exact}
                sx={{
                  ...navButtonStyle,
                  ...(isLoggedIn
                    ? {}
                    : { fontSize: "1.15rem", px: 3, py: 2, height: 56 }),
                }}
                startIcon={link.icon}
                onClick={() => isMobile && onClose()}
              >
                <span className="nav-btn-text">{link.text}</span>
              </Button>
            ))}

            {/* Structure Dropdown */}
            <Button
              sx={navButtonStyle}
              startIcon={<AccountTreeOutlinedIcon fontSize="small" />}
              onClick={(e) => setStructuresAnchor(e.currentTarget)}
              endIcon={<ExpandMoreIcon fontSize="small" />}
            >
              <span className="nav-btn-text">
                {intl.formatMessage({
                  defaultMessage: "Structure",
                  id: "rihWTl",
                })}
              </span>
            </Button>
            <Menu
              anchorEl={structuresAnchor}
              open={Boolean(structuresAnchor)}
              onClose={() => setStructuresAnchor(null)}
              PaperProps={{
                sx: {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  borderRadius: "8px",
                  minWidth: 200,
                },
              }}
            >
              {map(STRUCTURES, ({ name, link }) => (
                <MenuItem
                  key={name}
                  onClick={() => {
                    openLink(link);
                    setStructuresAnchor(null);
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Icon
                    icon={
                      name.includes("Calendar")
                        ? ["far", "calendar-day"]
                        : ["fad", "trophy"]
                    }
                    size="sm"
                  />
                  <Typography sx={{ ml: 1.5, fontSize: "0.9rem" }}>
                    {name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>

            {/* Support Dropdown */}
            <Button
              sx={navButtonStyle}
              startIcon={<HeadsetMicOutlinedIcon fontSize="small" />}
              onClick={(e) => setSupportAnchor(e.currentTarget)}
              endIcon={<ExpandMoreIcon fontSize="small" />}
            >
              <span className="nav-btn-text">
                {intl.formatMessage({
                  defaultMessage: "Support",
                  id: "HqRNN8",
                })}
              </span>
            </Button>
            <Menu
              anchorEl={supportAnchor}
              open={Boolean(supportAnchor)}
              onClose={() => setSupportAnchor(null)}
              PaperProps={{
                sx: {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  borderRadius: "8px",
                  minWidth: 200,
                },
              }}
            >
              {map(SUPPORT, ({ name, link }) => (
                <MenuItem
                  key={name}
                  onClick={() => {
                    openLink(link);
                    setSupportAnchor(null);
                  }}
                  sx={{
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Icon icon={["fas", "film"]} size="sm" />
                  <Typography sx={{ ml: 1.5, fontSize: "0.9rem" }}>
                    {name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>

            {/* Contact */}
            <Button
              component={NavLink}
              to="/contact"
              exact
              sx={navButtonStyle}
              startIcon={<MailOutlineOutlinedIcon fontSize="small" />}
              onClick={() => isMobile && onClose()}
            >
              <span className="nav-btn-text">
                {intl.formatMessage({
                  defaultMessage: "Contact",
                  id: "zFegDD",
                })}
              </span>
            </Button>

            {/* Rankings Dropdown */}
            <Button
              sx={navButtonStyle}
              startIcon={<BarChartOutlinedIcon fontSize="small" />}
              onClick={(e) => setRankingsAnchor(e.currentTarget)}
              endIcon={<ExpandMoreIcon fontSize="small" />}
            >
              <span className="nav-btn-text">
                {intl.formatMessage({
                  defaultMessage: "Rankings",
                  id: "2zwpJn",
                })}
              </span>
            </Button>
            {/* Team Register Button */}
            <Button
              component={NavLink}
              to="/team/register"
              sx={navButtonStyle}
              startIcon={<PersonAddOutlinedIcon fontSize="small" />}
              onClick={() => isMobile && onClose()}
            >
              <span className="nav-btn-text">
                {intl.formatMessage({
                  defaultMessage: "Team Register",
                  id: "Oiyyzr",
                })}
              </span>
            </Button>
            <Menu
              anchorEl={rankingsAnchor}
              open={Boolean(rankingsAnchor)}
              onClose={() => setRankingsAnchor(null)}
              PaperProps={{
                sx: {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: "blur(20px)",
                  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  borderRadius: "8px",
                  minWidth: 180,
                },
              }}
            >
              <MenuItem
                component={NavLink}
                to="/players"
                onClick={() => setRankingsAnchor(null)}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.9rem" }}>
                  <FormattedMessage defaultMessage="Players" id="/l0DrF" />
                </Typography>
              </MenuItem>
              <MenuItem
                component={NavLink}
                to="/teams"
                onClick={() => setRankingsAnchor(null)}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.9rem" }}>
                  <FormattedMessage defaultMessage="Teams" id="DNeuzb" />
                </Typography>
              </MenuItem>
              <MenuItem
                component={NavLink}
                to="/hallOfFame"
                onClick={() => setRankingsAnchor(null)}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Typography sx={{ fontSize: "0.9rem" }}>
                  <FormattedMessage defaultMessage="Hall of Fame" id="9VjriH" />
                </Typography>
              </MenuItem>
            </Menu>
          </Box>

          {/* Desktop Auth/Tools Section */}
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            {isLoggedIn && currentUserDetails && (
              <Tools
                openLanguageMenu={openLanguageMenu}
                onOpenChat={onOpenChat}
              />
            )}
            {!isLoggedIn && (
              <Box className="flex justify-end gap-lg mg-r-md">
                <Button
                  component={NavLink}
                  to="/register"
                  variant="contained"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 3,
                    py: 1,
                    boxShadow: "none",
                    background: (theme) =>
                      `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                    color: (theme) => theme.palette.primary.contrastText,
                    "&:hover": {
                      background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                      color: (theme) => theme.palette.primary.contrastText,
                    },
                  }}
                >
                  <FormattedMessage defaultMessage="Sign Up" id="39AHJm" />
                </Button>
                <Button
                  component={NavLink}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 3,
                    py: 1,
                    boxShadow: "none",
                    borderColor: (theme) => theme.palette.primary.main,
                    color: (theme) => theme.palette.primary.main,
                    background: "transparent",
                    "&:hover": {
                      background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.primary.main}11 0%, ${theme.palette.primary.light}11 100%)`,
                      borderColor: (theme) => theme.palette.primary.light,
                      color: (theme) => theme.palette.primary.light,
                    },
                  }}
                >
                  <FormattedMessage defaultMessage="Sign In" id="Ub+AGc" />
                </Button>
              </Box>
            )}
          </Box>

          {/* Mobile Close Button */}
          {!isDesktop && (
            <IconButton
              onClick={onClose}
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: theme.palette.primary.main,

                backgroundColor: "transparent",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <Icon icon={["fal", "times"]} />
            </IconButton>
          )}
        </Box>
      </Container>

      {/* Mobile Navigation Section */}
      <Box
        sx={{
          display: isDesktop ? "none" : "flex",
          flexDirection: "column",
          gap: 2,
          mt: 3,
          width: "100%",
        }}
      >
        {map(tabs, (link: LinkProps) =>
          // Hide "My Calendar" for not logged in users
          !isLoggedIn && link.to === "/team/calendar" ? null : (
            <Button
              key={link.to}
              component={NavLink}
              to={link.to}
              exact={link.exact}
              sx={{
                color: theme.palette.text.primary,
                justifyContent: "flex-start",
                fontWeight: 500,
                fontSize: "1rem",
                borderRadius: "6px",
                px: 2,
                py: 1.5,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                },
                "&.active": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              }}
              startIcon={link.icon}
              onClick={onClose}
              fullWidth
            >
              {link.text}
            </Button>
          )
        )}

        <Button
          sx={{
            color: theme.palette.text.primary,
            justifyContent: "flex-start",
            fontWeight: 500,
            fontSize: "1rem",
            borderRadius: "6px",
            px: 2,
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            },
            "&.active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
          startIcon={<AccountTreeOutlinedIcon fontSize="small" />}
          onClick={(e) => setStructuresAnchor(e.currentTarget)}
          fullWidth
          endIcon={<ExpandMoreIcon fontSize="small" />}
        >
          <span>
            {intl.formatMessage({
              defaultMessage: "Structure",
              id: "rihWTl",
            })}
          </span>
        </Button>
        <Menu
          anchorEl={structuresAnchor}
          open={Boolean(structuresAnchor)}
          onClose={() => setStructuresAnchor(null)}
          PaperProps={{
            sx: {
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: "blur(20px)",
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              borderRadius: "8px",
              minWidth: 200,
            },
          }}
        >
          {map(STRUCTURES, ({ name, link }) => (
            <MenuItem
              key={name}
              onClick={() => {
                openLink(link);
                setStructuresAnchor(null);
                onClose();
              }}
              sx={{
                py: 1.5,
                px: 2,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <Icon
                icon={
                  name.includes("Calendar")
                    ? ["far", "calendar-day"]
                    : ["fad", "trophy"]
                }
                size="sm"
              />
              <Typography sx={{ ml: 1.5, fontSize: "0.9rem" }}>
                {name}
              </Typography>
            </MenuItem>
          ))}
        </Menu>

        <Button
          sx={{
            color: theme.palette.text.primary,
            justifyContent: "flex-start",
            fontWeight: 500,
            fontSize: "1rem",
            borderRadius: "6px",
            px: 2,
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            },
            "&.active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
          startIcon={<BarChartOutlinedIcon fontSize="small" />}
          onClick={(e) => setRankingsAnchor(e.currentTarget)}
          fullWidth
          endIcon={<ExpandMoreIcon fontSize="small" />}
        >
          <span>
            {intl.formatMessage({
              defaultMessage: "Rankings",
              id: "2zwpJn",
            })}
          </span>
        </Button>
        <Menu
          anchorEl={rankingsAnchor}
          open={Boolean(rankingsAnchor)}
          onClose={() => setRankingsAnchor(null)}
          PaperProps={{
            sx: {
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: "blur(20px)",
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              borderRadius: "8px",
              minWidth: 180,
            },
          }}
        >
          <MenuItem
            component={NavLink}
            to="/players"
            onClick={() => {
              setRankingsAnchor(null);
              onClose();
            }}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Typography sx={{ fontSize: "0.9rem" }}>
              <FormattedMessage defaultMessage="Players" id="/l0DrF" />
            </Typography>
          </MenuItem>
          <MenuItem
            component={NavLink}
            to="/teams"
            onClick={() => {
              setRankingsAnchor(null);
              onClose();
            }}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Typography sx={{ fontSize: "0.9rem" }}>
              <FormattedMessage defaultMessage="Teams" id="DNeuzb" />
            </Typography>
          </MenuItem>
          <MenuItem
            component={NavLink}
            to="/hallOfFame"
            onClick={() => {
              setRankingsAnchor(null);
              onClose();
            }}
            sx={{
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Typography sx={{ fontSize: "0.9rem" }}>
              <FormattedMessage defaultMessage="Hall of Fame" id="9VjriH" />
            </Typography>
          </MenuItem>
        </Menu>

        {/* Team Register (mobile) */}
        <Button
          component={NavLink}
          to="/team/register"
          exact
          sx={{
            color: theme.palette.text.primary,
            justifyContent: "flex-start",
            fontWeight: 500,
            fontSize: "1rem",
            borderRadius: "6px",
            px: 2,
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            },
            "&.active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
          startIcon={<PersonAddOutlinedIcon fontSize="small" />}
          onClick={onClose}
          fullWidth
        >
          <FormattedMessage defaultMessage="Team Register" id="Oiyyzr" />
        </Button>
        {isLoggedIn && (
          <Button
            component={NavLink}
            to="/dashboard/stats"
            exact
            sx={{
              color: theme.palette.text.primary,
              justifyContent: "flex-start",
              fontWeight: 500,
              fontSize: "1rem",
              borderRadius: "6px",
              px: 2,
              py: 1.5,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
              "&.active": {
                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                fontWeight: 600,
              },
            }}
            startIcon={<BarChartOutlinedIcon fontSize="small" />}
            onClick={onClose}
            fullWidth
          >
            <FormattedMessage defaultMessage="Stats" id="U86B6w" />
          </Button>
        )}

        <Button
          component={NavLink}
          to="/contact"
          exact
          sx={{
            color: theme.palette.text.primary,
            justifyContent: "flex-start",
            fontWeight: 500,
            fontSize: "1rem",
            borderRadius: "6px",
            px: 2,
            py: 1.5,
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            },
            "&.active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 600,
            },
          }}
          startIcon={<MailOutlineOutlinedIcon fontSize="small" />}
          onClick={onClose}
          fullWidth
        >
          <FormattedMessage defaultMessage="Contact" id="zFegDD" />
        </Button>
      </Box>
    </Paper>
  );
};

export const Header: React.FC<{ onOpenChat: () => void }> = ({
  onOpenChat,
}) => {
  const [open, setOpen] = useState(false);
  const [openLangMenu, setOpenLangMenu] = useState(false);

  const theme = useTheme();
  const isLoggedIn = useIsLoggedIn();
  const isDesktop = useMediaQuery("(min-width:900px)");

  const openLanguageMenu = () => setOpenLangMenu(true);
  const closeLanguageMenu = () => setOpenLangMenu(false);

  return (
    <>
      <SelectLanguage onClose={closeLanguageMenu} isOpened={openLangMenu} />

      {!isDesktop && (
        <a.div
          className="nav"
          style={{
            left: open ? "0%" : "-100%",
            position: "fixed",
            top: 0,
            bottom: 0,
            width: "100vw",
            zIndex: 1200,
            transition: "left 0.3s",
          }}
        >
          <NavigationMenu
            onClose={() => setOpen(false)}
            openLanguageMenu={openLanguageMenu}
            onOpenChat={onOpenChat}
            isDesktop={false}
          />
        </a.div>
      )}

      {/* Mobile Header */}
      {!isDesktop && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            backgroundColor: alpha(theme.palette.background.default, 0.95),
            backdropFilter: "blur(20px)",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              color: theme.palette.text.primary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              },
            }}
          >
            <Icon icon={["fal", "bars"]} />
          </IconButton>

          {isLoggedIn && (
            <Tools
              openLanguageMenu={openLanguageMenu}
              onOpenChat={onOpenChat}
            />
          )}
          {!isLoggedIn && (
            <Box className="flex gap-md">
              <Button
                component={NavLink}
                to="/register"
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  boxShadow: "none",
                  background: (theme) =>
                    `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                  color: (theme) => theme.palette.primary.contrastText,
                  "&:hover": {
                    background: (theme) =>
                      `linear-gradient(90deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    color: (theme) => theme.palette.primary.contrastText,
                  },
                }}
              >
                <FormattedMessage defaultMessage="Sign Up" id="39AHJm" />
              </Button>
              <Button
                component={NavLink}
                to="/login"
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  boxShadow: "none",
                  borderColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.main,
                  background: "transparent",
                  "&:hover": {
                    background: (theme) =>
                      `linear-gradient(90deg, ${theme.palette.primary.main}11 0%, ${theme.palette.primary.light}11 100%)`,
                    borderColor: (theme) => theme.palette.primary.light,
                    color: (theme) => theme.palette.primary.light,
                  },
                }}
              >
                <FormattedMessage defaultMessage="Sign In" id="Ub+AGc" />
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Desktop Header */}
      {isDesktop && (
        <NavigationMenu
          onClose={undefined}
          openLanguageMenu={openLanguageMenu}
          onOpenChat={onOpenChat}
          isDesktop={true}
        />
      )}

      <HeaderAnnouncements />
    </>
  );
};

export default Header;
